import { useState } from 'react';
import { useLanguage } from '@/context/LanguageContext';
import { Download, Play, ZoomIn } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';

const PDF_URL = '/project/anteproyecto.pdf';

const RENDERS = [
  {
    url: '/project/render-1.jpg',
    labelEn: 'Rear Facade with Pool & Waterfall',
    labelEs: 'Fachada Trasera con Piscina y Cascada'
  },
  {
    url: '/project/render-2.jpg',
    labelEn: 'Main Entrance & Front Facade',
    labelEs: 'Entrada Principal y Fachada Frontal'
  },
  {
    url: '/project/render-3.jpg',
    labelEn: 'Aerial View with Infinity Pool',
    labelEs: 'Vista A\u00E9rea con Piscina Infinita'
  },
  {
    url: '/project/render-4.jpg',
    labelEn: 'Parking & Lateral View',
    labelEs: 'Garaje y Vista Lateral'
  }
];

const DRONE_PHOTOS = [
  {
    url: '/project/drone-1.jpg',
    labelEn: 'Aerial View - Hacienda Las Chapas & Mountains',
    labelEs: 'Vista A\u00E9rea - Hacienda Las Chapas y Monta\u00F1as'
  },
  {
    url: '/project/drone-2.jpg',
    labelEn: 'Aerial View - Plot & Surroundings',
    labelEs: 'Vista A\u00E9rea - Parcela y Alrededores'
  },
  {
    url: '/project/drone-3.jpg',
    labelEn: 'Aerial View - Plot Close-Up',
    labelEs: 'Vista A\u00E9rea - Parcela de Cerca'
  },
  {
    url: '/project/drone-4.jpg',
    labelEn: 'Aerial View - Sea Views from the Plot',
    labelEs: 'Vista A\u00E9rea - Vistas al Mar desde la Parcela'
  },
  {
    url: '/project/parcela.jpg',
    labelEn: 'The Plot - 1,925 m\u00B2 Delimited',
    labelEs: 'La Parcela - 1.925 m\u00B2 Delimitada'
  }
];

const FLOOR_PLANS = [
  { url: '/project/basement.jpg', labelEn: 'Basement Plan', labelEs: 'Planta S\u00F3tano' },
  { url: '/project/ground-floor.jpg', labelEn: 'Ground Floor Plan', labelEs: 'Planta Baja' },
  { url: '/project/first-floor.jpg', labelEn: 'First Floor Plan', labelEs: 'Planta Primera' },
  { url: '/project/section-transversal-1.jpg', labelEn: 'Cross Section 1', labelEs: 'Secci\u00F3n Transversal 1' },
  { url: '/project/section-transversal-2.jpg', labelEn: 'Cross Section 2', labelEs: 'Secci\u00F3n Transversal 2' },
  { url: '/project/sections.jpg', labelEn: 'Longitudinal Sections', labelEs: 'Secciones Longitudinales' },
  { url: '/project/occupancy.jpg', labelEn: 'Occupancy Plan', labelEs: 'Plano de Ocupaci\u00F3n' },
  { url: '/project/buildability.jpg', labelEn: 'Buildability Plan', labelEs: 'Plano de Edificabilidad' }
];

function ImageCard({ img, lang, onClick, className = '' }) {
  return (
    <div
      className={`group relative overflow-hidden cursor-pointer bg-[#0a0a0a] ${className}`}
      onClick={() => onClick(img)}
    >
      <img
        src={img.url}
        alt={lang === 'en' ? img.labelEn : img.labelEs}
        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-[1.5s] ease-out"
        loading="lazy"
      />
      <div className="absolute inset-0 bg-black/10 group-hover:bg-black/30 transition-colors duration-500" />
      <div className="absolute inset-0 shadow-[inset_0_0_30px_rgba(5,5,5,0.4)]" />
      <div className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center bg-black/40 backdrop-blur-sm opacity-0 group-hover:opacity-100 transition-opacity">
        <ZoomIn className="w-4 h-4 text-white" />
      </div>
      <div className="absolute bottom-0 left-0 right-0 p-3 md:p-4 bg-gradient-to-t from-black/60 to-transparent translate-y-2 group-hover:translate-y-0 opacity-0 group-hover:opacity-100 transition-all duration-500">
        <p className="text-xs md:text-sm font-light text-[#F5F5F0]">
          {lang === 'en' ? img.labelEn : img.labelEs}
        </p>
      </div>
    </div>
  );
}

export default function GallerySection() {
  const { lang, t } = useLanguage();
  const [lightboxImg, setLightboxImg] = useState(null);
  const [showPlans, setShowPlans] = useState(false);

  return (
    <section id="gallery" className="py-24 md:py-32 lg:py-40 bg-[#0f1115]" data-testid="gallery-section">
      <div className="px-6 md:px-12 lg:px-24 max-w-[1600px] mx-auto">
        {/* Section Header */}
        <div className="text-center mb-16 md:mb-20 space-y-4">
          <p className="text-xs font-medium uppercase tracking-[0.2em] text-[#C8B598]">
            {t('gallery.overline')}
          </p>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight text-[#F5F5F0] leading-tight">
            {t('gallery.title')}
          </h2>
          <p className="text-base font-light text-[#9CA3AF] max-w-2xl mx-auto">
            {t('gallery.subtitle')}
          </p>
          <div className="flex items-center justify-center gap-2 pt-2">
            <span className="text-xs text-[#9CA3AF]">{lang === 'en' ? 'Designed by' : 'Dise\u00F1ado por'}</span>
            <a
              href="https://www.ismaelmerida.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs font-medium text-[#C8B598] hover:text-[#E2D1B8] transition-colors underline underline-offset-4"
              data-testid="architect-link"
            >
              Ismael M&#233;rida
            </a>
          </div>
        </div>

        {/* === RENDERS === */}
        <div className="mb-16">
          <p className="text-xs font-medium uppercase tracking-[0.2em] text-[#C8B598] mb-6">
            {lang === 'en' ? 'Architectural Renders' : 'Renders Arquitect\u00F3nicos'}
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="gallery-grid">
            {RENDERS.map((img, i) => (
              <ImageCard
                key={i}
                img={img}
                lang={lang}
                onClick={setLightboxImg}
                className="aspect-[4/3]"
              />
            ))}
          </div>
        </div>

        {/* === DRONE PHOTOS === */}
        <div className="mb-16">
          <p className="text-xs font-medium uppercase tracking-[0.2em] text-[#C8B598] mb-6">
            {lang === 'en' ? 'Drone Photography' : 'Fotograf\u00EDa con Dron'}
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="drone-photos-grid">
            {DRONE_PHOTOS.slice(0, 4).map((img, i) => (
              <ImageCard
                key={i}
                img={img}
                lang={lang}
                onClick={setLightboxImg}
                className="aspect-square"
              />
            ))}
          </div>
          {/* Parcela - full width */}
          <div className="mt-3">
            <div
              className="group relative overflow-hidden cursor-pointer bg-[#0a0a0a]"
              onClick={() => setLightboxImg(DRONE_PHOTOS[4])}
              data-testid="gallery-parcela"
            >
              <img
                src={DRONE_PHOTOS[4].url}
                alt={lang === 'en' ? DRONE_PHOTOS[4].labelEn : DRONE_PHOTOS[4].labelEs}
                className="w-full h-auto max-h-[500px] object-contain mx-auto group-hover:scale-[1.02] transition-transform duration-[1.5s] ease-out"
                loading="lazy"
              />
              <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/70 to-transparent">
                <p className="text-sm font-light text-[#F5F5F0] text-center">
                  {lang === 'en' ? DRONE_PHOTOS[4].labelEn : DRONE_PHOTOS[4].labelEs}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* === DRONE VIDEO === */}
        <div className="mb-16">
          <p className="text-xs font-medium uppercase tracking-[0.2em] text-[#C8B598] mb-6">
            {t('gallery.droneTitle')}
          </p>
          <div className="border border-white/5" data-testid="drone-video-section">
            <video
              controls
              preload="metadata"
              className="w-full max-h-[500px] bg-black"
              poster="/project/render-1.jpg"
              data-testid="drone-video"
            >
              <source src="/project/drone-video.mp4" type="video/mp4" />
            </video>
          </div>
        </div>

        {/* === FLOOR PLANS === */}
        <div className="mb-12">
          <button
            onClick={() => setShowPlans(!showPlans)}
            className="w-full flex items-center justify-between p-6 bg-[#050505] border border-white/5 hover:border-[#C8B598]/20 transition-colors"
            data-testid="toggle-floor-plans"
          >
            <span className="text-sm font-medium text-[#F5F5F0] uppercase tracking-wider">
              {lang === 'en' ? 'Architectural Plans' : 'Planos Arquitect\u00F3nicos'}
            </span>
            <span className={`text-[#C8B598] text-xl transition-transform duration-300 ${showPlans ? 'rotate-45' : ''}`}>
              +
            </span>
          </button>

          {showPlans && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3 animate-fadeIn" data-testid="floor-plans-grid">
              {FLOOR_PLANS.map((plan, i) => (
                <div
                  key={i}
                  className="group relative overflow-hidden cursor-pointer bg-white aspect-[4/3]"
                  onClick={() => setLightboxImg(plan)}
                  data-testid={`floor-plan-${i}`}
                >
                  <img
                    src={plan.url}
                    alt={lang === 'en' ? plan.labelEn : plan.labelEs}
                    className="w-full h-full object-contain p-2 group-hover:scale-105 transition-transform duration-700"
                    loading="lazy"
                  />
                  <div className="absolute bottom-0 left-0 right-0 p-2 bg-[#050505]/90">
                    <p className="text-[11px] font-light text-[#F5F5F0] text-center">
                      {lang === 'en' ? plan.labelEn : plan.labelEs}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Download PDF */}
        <div className="text-center">
          <a
            href={PDF_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-3 px-8 py-4 bg-transparent border border-[#C8B598] text-[#C8B598] font-medium tracking-wide transition-all duration-300 hover:bg-[#C8B598] hover:text-[#050505] text-sm"
            data-testid="download-pdf-button"
          >
            <Download className="w-4 h-4" />
            {t('gallery.downloadPdf')}
          </a>
        </div>
      </div>

      {/* Lightbox */}
      <Dialog open={!!lightboxImg} onOpenChange={() => setLightboxImg(null)}>
        <DialogContent className="max-w-5xl bg-[#0f1115] border-white/10 p-2" data-testid="gallery-lightbox">
          <DialogTitle className="sr-only">
            {lightboxImg ? (lang === 'en' ? lightboxImg.labelEn : lightboxImg.labelEs) : 'Image'}
          </DialogTitle>
          {lightboxImg && (
            <div>
              <img
                src={lightboxImg.url}
                alt={lang === 'en' ? lightboxImg.labelEn : lightboxImg.labelEs}
                className="w-full h-auto max-h-[80vh] object-contain"
              />
              <p className="text-center text-sm text-[#9CA3AF] mt-3 pb-2">
                {lang === 'en' ? lightboxImg.labelEn : lightboxImg.labelEs}
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}
